import { API_ENDPOINTS, getApiOrigin } from "@/config/api";

const ABSOLUTE_URL_REGEXP = /^https?:\/\//i;
const PROTOCOL_RELATIVE_REGEXP = /^\/\//;
const STORAGE_KEY_REGEXP = /^(img|music)\/([^/]+)$/i;
const CONTENT_PATH_REGEXP = /\/file\/content\/(img|music)\/([^/?#]+)$/i;
const LEGACY_DOWNLOAD_PATH_REGEXP = /\/file\/download$/i;
const IMAGE_EXTENSION_REGEXP = /\.(jpe?g|png|gif|bmp|webp)$/i;
const MUSIC_EXTENSION_REGEXP = /\.(mp3|wav|flac|aac|ogg)$/i;

function toAbsoluteUrl(path) {
  const relativePath = String(path || "").replace(/^\/+/, "");
  return new URL(relativePath, `${getApiOrigin()}/`).toString();
}

function isCurrentApiUrl(parsedUrl) {
  try {
    return parsedUrl.origin === new URL(getApiOrigin()).origin;
  } catch (error) {
    return false;
  }
}

function inferStorageType(fileName) {
  if (IMAGE_EXTENSION_REGEXP.test(fileName || "")) return "img";
  if (MUSIC_EXTENSION_REGEXP.test(fileName || "")) return "music";
  return "";
}

function legacyUrlToKey(parsedUrl) {
  if (!LEGACY_DOWNLOAD_PATH_REGEXP.test(parsedUrl.pathname)) return "";
  const fileName = parsedUrl.searchParams.get("name") || "";
  const storageType = inferStorageType(fileName);
  return storageType && fileName ? `${storageType}/${fileName}` : "";
}

function normalizeAbsoluteUrl(value) {
  try {
    const parsed = new URL(value);
    if (!isCurrentApiUrl(parsed)) return value;

    const contentMatch = parsed.pathname.match(CONTENT_PATH_REGEXP);
    if (contentMatch) {
      return buildStoredFileUrl(
        `${contentMatch[1].toLowerCase()}/${decodeURIComponent(contentMatch[2])}`
      );
    }

    const legacyKey = legacyUrlToKey(parsed);
    if (legacyKey) return buildStoredFileUrl(legacyKey);
  } catch (error) {
    return value;
  }
  return value;
}

export function buildStoredFileUrl(storageKey) {
  const match = String(storageKey || "").replace(/^\/+/, "").match(STORAGE_KEY_REGEXP);
  if (!match) return "";
  return toAbsoluteUrl(API_ENDPOINTS.file.content(match[1].toLowerCase(), match[2]));
}

/** 将稳定 key、新内容地址和旧下载地址统一转换为 /file/content 内容地址。 */
export function normalizeFileUrl(value) {
  if (!value) return "";
  const normalizedValue = String(value).trim();
  if (!normalizedValue) return "";
  if (ABSOLUTE_URL_REGEXP.test(normalizedValue)) return normalizeAbsoluteUrl(normalizedValue);
  if (PROTOCOL_RELATIVE_REGEXP.test(normalizedValue)) {
    const protocol = typeof window === "undefined" ? "https:" : window.location.protocol;
    return normalizeAbsoluteUrl(`${protocol}${normalizedValue}`);
  }

  const storedFileUrl = buildStoredFileUrl(normalizedValue);
  if (storedFileUrl) return storedFileUrl;

  const storageType = inferStorageType(normalizedValue);
  if (storageType && !normalizedValue.includes("/")) {
    return buildStoredFileUrl(`${storageType}/${normalizedValue}`);
  }

  if (normalizedValue.startsWith("/")) {
    return normalizeAbsoluteUrl(toAbsoluteUrl(normalizedValue));
  }
  return normalizedValue;
}

/** 持久化时只返回 img/... 或 music/... key，绝不保存域名和下载 URL。 */
export function toStoredFileValue(value) {
  if (!value) return "";
  const rawValue = String(value).trim();
  if (!rawValue) return "";

  const directKey = rawValue.replace(/^\/+/, "").match(STORAGE_KEY_REGEXP);
  if (directKey) return `${directKey[1].toLowerCase()}/${directKey[2]}`;

  if (!rawValue.includes("/")) {
    const storageType = inferStorageType(rawValue);
    return storageType ? `${storageType}/${rawValue}` : rawValue;
  }

  const isExternalCandidate =
    ABSOLUTE_URL_REGEXP.test(rawValue) || PROTOCOL_RELATIVE_REGEXP.test(rawValue);
  let parsed;
  try {
    const normalizedInput = PROTOCOL_RELATIVE_REGEXP.test(rawValue)
      ? `${typeof window === "undefined" ? "https:" : window.location.protocol}${rawValue}`
      : rawValue;
    parsed = new URL(normalizedInput, `${getApiOrigin()}/`);
  } catch (error) {
    return rawValue;
  }

  if (isExternalCandidate && !isCurrentApiUrl(parsed)) return rawValue;

  const contentMatch = parsed.pathname.match(CONTENT_PATH_REGEXP);
  if (contentMatch) {
    return `${contentMatch[1].toLowerCase()}/${decodeURIComponent(contentMatch[2])}`;
  }
  return legacyUrlToKey(parsed) || rawValue;
}

export function normalizeMediaFields(record, fields = []) {
  if (!record || typeof record !== "object") return record;
  const nextRecord = { ...record };
  fields.forEach((field) => {
    if (nextRecord[field]) nextRecord[field] = normalizeFileUrl(nextRecord[field]);
  });
  return nextRecord;
}

export function normalizeMediaList(list, fields = []) {
  if (!Array.isArray(list)) return [];
  return list.map((item) => normalizeMediaFields(item, fields));
}
